/// Per-tick advancement for moving airports (carriers).
///
/// `centerCoordinate` and runway endpoint coordinates on the
/// `Airport` object are mutated in place each tick by the GameSession
/// motion pass. Downstream consumers (touchdown detection, approach
/// course, runway selection, render layers) read those coordinates
/// fresh each frame, so the same code paths that drive static
/// airports drive moving airports too — only the values shift.

import type { Airport, GeoCoordinate, Runway } from '../types/LevelData'

/// Advance a geographic coordinate `distanceNM` nautical miles
/// along compass-heading `headingDeg`. Approximation valid for
/// the small per-tick steps the carrier sim takes (~tens of
/// metres per second × tick dt = sub-arcsecond shifts), so we
/// don't need the full great-circle math.
export function advanceCoordinate(
  c: GeoCoordinate,
  headingDeg: number,
  distanceNM: number,
): GeoCoordinate {
  // Compass: 0° = North, 90° = East. Standard horizontal/vertical:
  //   dN = cos(heading), dE = sin(heading)
  // 1 NM ≈ 1/60° of latitude.
  // 1 NM ≈ 1/(60·cos(lat))° of longitude.
  const headingRad = (headingDeg * Math.PI) / 180
  const dN = distanceNM * Math.cos(headingRad)
  const dE = distanceNM * Math.sin(headingRad)
  const lat = c.latitude + dN / 60
  const cosLat = Math.cos((c.latitude * Math.PI) / 180)
  const lon = c.longitude + dE / (60 * Math.max(cosLat, 1e-6))
  return { latitude: lat, longitude: lon }
}

/// State a session keeps per moving airport. The initial coordinates
/// are captured once at session construction; per-tick advances
/// translate the airport's `centerCoordinate` and all runway
/// endpoints by the cumulative offset.
export interface MovingAirportState {
  initialCenter: GeoCoordinate
  initialRunways: Array<{ start: GeoCoordinate; end: GeoCoordinate }>
}

export function captureInitialAirportState(airport: Airport): MovingAirportState {
  return {
    initialCenter: { ...airport.centerCoordinate },
    initialRunways: airport.runways.map((r) => ({
      start: { ...r.startCoordinate },
      end: { ...r.endCoordinate },
    })),
  }
}

/// Mutates `airport` so its `centerCoordinate` and all runway
/// endpoint coordinates reflect the new live position. The
/// motion pass uses the initial state captured at session start
/// to translate every coordinate by the same delta — the runway
/// shape is welded to the airport, so they all shift together.
export function applyAirportMotion(
  airport: Airport,
  initial: MovingAirportState,
  scaledDtSec: number,
): void {
  if (!airport.motion) return
  const distNM = (airport.motion.speedKts / 3600) * scaledDtSec
  const newCenter = advanceCoordinate(
    airport.centerCoordinate,
    airport.motion.headingDeg,
    distNM,
  )
  const dLat = newCenter.latitude - initial.initialCenter.latitude
  const dLon = newCenter.longitude - initial.initialCenter.longitude
  airport.centerCoordinate = newCenter
  for (let i = 0; i < airport.runways.length; i++) {
    const r = airport.runways[i] as Runway
    const init = initial.initialRunways[i]
    if (!init) continue
    r.startCoordinate = {
      latitude: init.start.latitude + dLat,
      longitude: init.start.longitude + dLon,
    }
    r.endCoordinate = {
      latitude: init.end.latitude + dLat,
      longitude: init.end.longitude + dLon,
    }
  }
}
